import { motion } from "framer-motion";
import { ArrowRight, Activity, Globe2, Box, Cpu } from "lucide-react";

export const Hero = () => {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden bg-[#203864] text-white"
    >
      {/* tech grid */}
      <div className="absolute inset-0 tech-grid-bg-dark opacity-60" />

      {/* circuit svg */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        preserveAspectRatio="none"
        viewBox="0 0 1400 800"
      >
        <path className="circuit-line" d="M0 120 L320 120 L380 180 L640 180 L700 120 L1100 120 L1160 180 L1400 180" />
        <path className="circuit-line alt" d="M0 700 L260 700 L320 640 L520 640 L580 700 L880 700 L940 640 L1400 640" />
        <path className="circuit-line" d="M180 0 L180 220 L280 320 L280 540 L380 640 L380 800" />
        <path className="circuit-line alt" d="M1200 0 L1200 240 L1100 340 L1100 520 L1200 620 L1200 800" />
        <circle className="circuit-pulse" cx="320" cy="120" r="4" />
        <circle className="circuit-pulse" cx="700" cy="120" r="4" style={{ animationDelay: "0.7s" }} />
        <circle className="circuit-pulse" cx="1160" cy="180" r="4" style={{ animationDelay: "1.4s" }} />
        <circle className="circuit-pulse" cx="380" cy="640" r="4" style={{ animationDelay: "1s" }} />
      </svg>

      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 mb-6 border border-[#843C0C] bg-[#843C0C]/15 px-3 py-1.5"
            data-testid="hero-badge"
          >
            <span className="h-1.5 w-1.5 bg-[#843C0C] animate-pulse" />
            <span className="font-mono-tech text-[10px] md:text-xs tracking-[0.28em] uppercase text-[#FCD7C5]">
              Importación · Logística · Comercialización
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.05]"
            data-testid="hero-title"
          >
            El puente directo entre la{" "}
            <span className="relative inline-block">
              <span className="text-[#FCD7C5]">innovación global</span>
              <span className="absolute -bottom-1 left-0 right-0 h-[3px] bg-[#843C0C]" />
            </span>{" "}
            y el mercado local.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-base md:text-lg text-white/80 max-w-2xl leading-relaxed"
            data-testid="hero-subtitle"
          >
            En Nexora Productos, somos el puente directo entre la innovación global y el
            mercado local, especializándonos en la importación y comercialización masiva
            de tecnología de vanguardia. Nos distinguimos en nuestro estado y en México en
            general por nuestra capacidad de anticipar tendencias, ofreciendo desde
            electrónica de consumo hasta soluciones complejas de telecomunicaciones con
            una logística impecable. No solo vendemos productos; entregamos
            competitividad y respaldo técnico, asegurando que cada cliente, mayorista o
            minorista, tenga acceso a lo mejor del mundo con un soporte local de
            excelencia.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <a
              href="#contacto"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 bg-[#843C0C] hover:bg-white hover:text-[#203864] text-white px-6 py-3.5 font-medium tracking-wide transition-all"
            >
              Solicitar cotización
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#servicios"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 border border-white/40 hover:border-white px-6 py-3.5 font-medium tracking-wide transition-colors"
            >
              Ver servicios
            </a>
          </motion.div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl" data-testid="hero-stats">
            {[
              { k: "Alcance", v: "Global" },
              { k: "Logística", v: "Propia" },
              { k: "Soporte", v: "Local 24/7" },
              { k: "Stock", v: "Inmediato" },
            ].map((s) => (
              <div key={s.k} className="border-l-2 border-[#843C0C] pl-3">
                <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-white/60">
                  {s.k}
                </div>
                <div className="font-semibold text-lg md:text-xl">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="lg:col-span-5"
        >
          <div className="relative tech-corners border border-white/15 bg-[#3B3838]/50 backdrop-blur p-6 md:p-8">
            <div className="flex items-center justify-between mb-5 pb-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Activity size={14} className="text-[#FCD7C5]" />
                <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-white/70">
                  Nexora · Live OPS
                </span>
              </div>
              <span className="font-mono-tech text-[10px] text-white/50">v.2025.12</span>
            </div>

            <div className="grid grid-cols-2 gap-px bg-white/10">
              {[
                { i: <Globe2 size={20} />, l: "Rutas activas", v: "12" },
                { i: <Box size={20} />, l: "SKUs en stock", v: "5,400+" },
                { i: <Cpu size={20} />, l: "Marcas distrib.", v: "40+" },
                { i: <Activity size={20} />, l: "Uptime soporte", v: "99.7%" },
              ].map((m, i) => (
                <div key={i} className="bg-[#203864] p-4">
                  <div className="text-[#FCD7C5] mb-2">{m.i}</div>
                  <div className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/60">
                    {m.l}
                  </div>
                  <div className="text-2xl font-bold mt-1">{m.v}</div>
                </div>
              ))}
            </div>

            <div className="mt-5 font-mono-tech text-[11px] text-white/60 leading-relaxed">
              <span className="text-[#FCD7C5]">{">"} </span>
              Inteligencia de mercado · scouting global · importación directa ·
              <span className="text-[#FCD7C5]"> _</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
